import React, { useRef, useState } from "react";
import { useTheme } from "next-themes";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import Avatar from "@/components/Avatar";
import { toast } from "sonner";
import { User, Mail, ShieldCheck, CalendarDays, Sun, Moon, Camera, Trash2, Loader2 } from "lucide-react";

const ROLE_LABELS = {
  admin: "Admin",
  mali_musavir: "Mali Müşavir",
  ofis_yoneticisi: "Ofis Yöneticisi",
  kidemli: "Kıdemli Personel",
  muhasebe: "Muhasebe Personeli",
  bordro: "Bordro Personeli",
  stajyer: "Stajyer",
  salt_okuma: "Salt Okuma",
};

const MAX_AVATAR_MB = 5;

export default function Settings() {
  const { user, setUser } = useAuth();
  const { theme, setTheme } = useTheme();
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const joinDate = user?.created_at
    ? new Date(user.created_at).toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" })
    : "-";

  const pickFile = () => fileInputRef.current?.click();

  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!/^image\/(jpeg|png|webp)$/.test(file.type)) {
      toast.error("Sadece JPG, PNG veya WEBP dosyaları kabul edilir");
      return;
    }
    if (file.size > MAX_AVATAR_MB * 1024 * 1024) {
      toast.error(`Dosya boyutu ${MAX_AVATAR_MB} MB sınırını aşıyor`);
      return;
    }
    const form = new FormData();
    form.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post("/users/me/avatar", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUser(data);
      toast.success("Profil fotoğrafı güncellendi");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Fotoğraf yüklenemedi");
    } finally {
      setUploading(false);
    }
  };

  const removePhoto = async () => {
    setUploading(true);
    try {
      const { data } = await api.delete("/users/me/avatar");
      setUser(data);
      toast.success("Profil fotoğrafı kaldırıldı");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Fotoğraf kaldırılamadı");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-5 fade-in">
      <div>
        <h1 className="font-head text-3xl font-semibold tracking-tight">Ayarlar</h1>
        <p className="text-sm text-muted-foreground mt-1">Profil bilgileriniz ve görünüm tercihleri</p>
      </div>

      <Card className="p-6">
        <h2 className="text-base font-semibold mb-4">Profil Bilgileri</h2>
        <div className="flex items-start gap-4">
          <div className="relative shrink-0">
            <Avatar
              user={user}
              className="w-16 h-16 rounded-full bg-primary/10 text-primary grid place-items-center font-head text-xl font-semibold"
            />
            <button
              data-testid="avatar-upload-btn"
              onClick={pickFile}
              disabled={uploading}
              title="Fotoğraf değiştir"
              className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-primary text-primary-foreground grid place-items-center border-2 border-card hover:opacity-90 transition-opacity disabled:opacity-60"
            >
              {uploading ? <Loader2 size={12} className="animate-spin" /> : <Camera size={12} />}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={onFileChange}
            />
          </div>
          <div className="space-y-2.5 min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <User size={15} className="text-muted-foreground shrink-0" />
              <span className="font-medium">{user?.name || "-"}</span>
            </div>
            <div className="flex items-center gap-2">
              <Mail size={15} className="text-muted-foreground shrink-0" />
              <span className="text-sm text-muted-foreground truncate">{user?.email || "-"}</span>
            </div>
            <div className="flex items-center gap-2">
              <ShieldCheck size={15} className="text-muted-foreground shrink-0" />
              <span className="text-sm text-muted-foreground">{ROLE_LABELS[user?.role] || user?.role || "-"}</span>
            </div>
            <div className="flex items-center gap-2">
              <CalendarDays size={15} className="text-muted-foreground shrink-0" />
              <span className="text-sm text-muted-foreground">Katılım: {joinDate}</span>
            </div>
            <div className="flex items-center gap-3 pt-1">
              <button
                data-testid="avatar-change-link"
                onClick={pickFile}
                disabled={uploading}
                className="text-xs font-medium text-primary hover:underline disabled:opacity-60"
              >
                Fotoğraf Değiştir
              </button>
              {user?.avatar_ext && (
                <button
                  data-testid="avatar-remove-link"
                  onClick={removePhoto}
                  disabled={uploading}
                  className="text-xs font-medium text-rose-600 hover:underline disabled:opacity-60 flex items-center gap-1"
                >
                  <Trash2 size={11} /> Kaldır
                </button>
              )}
            </div>
          </div>
        </div>
      </Card>

      <Card className="p-6">
        <h2 className="text-base font-semibold mb-4">Görünüm</h2>
        <p className="text-sm text-muted-foreground mb-4">Uygulamanın açık veya koyu temada görünmesini seçin.</p>
        <div className="flex gap-3">
          <button
            data-testid="settings-theme-light"
            onClick={() => setTheme("light")}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border text-sm font-medium transition-colors ${
              theme === "light" ? "border-primary bg-primary/10 text-primary" : "border-border hover:bg-accent text-muted-foreground"
            }`}
          >
            <Sun size={16} /> Açık
          </button>
          <button
            data-testid="settings-theme-dark"
            onClick={() => setTheme("dark")}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border text-sm font-medium transition-colors ${
              theme === "dark" ? "border-primary bg-primary/10 text-primary" : "border-border hover:bg-accent text-muted-foreground"
            }`}
          >
            <Moon size={16} /> Koyu
          </button>
        </div>
      </Card>
    </div>
  );
}
